import React, { useEffect, useState } from "react";
import { Button } from "@/components/common";
import { Send, Paperclip, Smile } from "lucide-react";
import { cn } from "@/utils/cn";

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  className?: string;
  initialValue?: string;
  onValueChange?: (value: string) => void;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  disabled = false,
  loading = false,
  placeholder = "Digite sua mensagem...",
  className = "",
  initialValue = "",
  onValueChange,
}) => {
  const [message, setMessage] = useState(initialValue);

  useEffect(() => {
    if (initialValue !== message) {
      setMessage(initialValue);
    }
  }, [initialValue]);

  const handleMessageChange = (value: string) => {
    setMessage(value);
    onValueChange?.(value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !disabled && !loading) {
      onSendMessage(message.trim());
      setMessage("");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  const isSendDisabled = !message.trim() || disabled || loading;

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        "flex items-end space-x-3 p-4 bg-white border-t border-gray-200",
        className
      )}
    >
      {/* Attachment Button */}
      <button
        type="button"
        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={disabled}
        aria-label="Anexar arquivo"
      >
        <Paperclip size={20} />
      </button>

      {/* Emoji Button */}
      <button
        type="button"
        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={disabled}
        aria-label="Inserir emoji"
      >
        <Smile size={20} />
      </button>

      {/* Message Input */}
      <div className="flex-1">
        <textarea
          value={message}
          onChange={(e) => handleMessageChange(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full min-h-[44px] max-h-32 px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
          rows={1}
          style={{
            minHeight: "44px",
            maxHeight: "128px",
          }}
        />
      </div>

      {/* Send Button */}
      <Button
        type="submit"
        variant="primary"
        size="md"
        disabled={isSendDisabled}
        loading={loading}
        className="px-4 py-2"
      >
        <Send size={16} />
      </Button>
    </form>
  );
};
